import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { coerceRequestStatus, REQUEST_STATUS_CLOSED } from '../Requests/types'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentOrganisation } from '../useCurrentOrganisation'

type ExistingRequest = RestSchemas['Messages.Requests.ExistingRequest']

type RequestRowActionsProps = {
  request: ExistingRequest
  listScope: 'organisation' | 'platform'
  onViewDetails: (request: ExistingRequest) => void
}

export function RequestRowActions({
  request,
  listScope,
  onViewDetails,
}: RequestRowActionsProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const { id: orgId } = useCurrentOrganisation()
  const {
    postRequestsApiCloseRequest,
    getRequestsApiGetOrganisationRequests,
    getRequestsApiGetPlatformRequests,
  } = useRestClient()

  const { mutate: closeRequest, isPending } = postRequestsApiCloseRequest.useMutation(undefined, {
    onSuccess: () => {
      if (listScope === 'organisation') {
        getRequestsApiGetOrganisationRequests.invalidateQueries({ parameters: { query: { orgId } } })
      } else {
        getRequestsApiGetPlatformRequests.invalidateQueries(undefined)
      }
      toast.success('Request closed')
    },
    onError: () => toast.error('Failed to close request'),
  })

  const canClose = coerceRequestStatus(request.status) !== REQUEST_STATUS_CLOSED

  const openDetail = () => {
    setMenuOpen(false)
    window.requestAnimationFrame(() => onViewDetails(request))
  }

  return (
    <>
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="size-8">
            <IconMenuOutline size={16} />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={openDetail}>View details</DropdownMenuItem>
          {canClose && (
            <DropdownMenuItem
              disabled={isPending}
              onSelect={() =>
                closeRequest({
                  path: { requestId: request.id! },
                })
              }
            >
              Close request
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
